import { Banknote, CreditCard, Fuel, Receipt, Users, Wallet } from 'lucide-react';

import { cn, formatCurrencyAdaptive, formatNumber } from '@/lib/utils';

import type { ConsolidadoTurnos } from '../turnos.types';

interface ConsolidadoTurnosCardProps {
  consolidado: ConsolidadoTurnos;
  className?: string;
}

interface CeldaProps {
  icon: typeof Users;
  label: string;
  value: string;
  hint?: string;
  tone?: 'brand' | 'emerald' | 'amber' | 'ink';
}

const TONES: Record<NonNullable<CeldaProps['tone']>, string> = {
  brand: 'bg-brand-50 text-brand-600',
  emerald: 'bg-emerald-50 text-emerald-600',
  amber: 'bg-amber-50 text-amber-600',
  ink: 'bg-ink-100 text-ink-600',
};

function Celda({ icon: Icon, label, value, hint, tone = 'brand' }: CeldaProps) {
  return (
    <div className="flex items-center gap-3 bg-white px-4 py-3.5">
      <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-xl', TONES[tone])}>
        <Icon className="size-4.5" aria-hidden="true" />
      </span>

      <div className="min-w-0">
        <p
          title={value}
          className="truncate font-mono text-base font-bold tracking-tight text-ink-900 tabular-nums"
        >
          {value}
        </p>
        <p className="truncate text-xs text-ink-500">{label}</p>
        {hint ? <p className="line-clamp-2 text-[11px] text-ink-400">{hint}</p> : null}
      </div>
    </div>
  );
}

/** Consolidado de todos los turnos abiertos. */
export function ConsolidadoTurnosCard({ consolidado, className }: ConsolidadoTurnosCardProps) {
  return (
    <div className={cn('grid gap-px overflow-hidden rounded-card border border-ink-100 bg-ink-100 sm:grid-cols-2 xl:grid-cols-3', className)}>
      <Celda
        icon={Users}
        label="Turnos abiertos"
        value={formatNumber(consolidado.turnos)}
        hint={`${consolidado.con_ventas} con ventas · ${consolidado.sin_movimiento} sin movimiento`}
        tone={consolidado.vencidos > 0 ? 'amber' : 'brand'}
      />
      <Celda
        icon={Wallet}
        label="Ventas acumuladas"
        value={formatCurrencyAdaptive(consolidado.monto_ventas)}
        hint={`${formatNumber(consolidado.transacciones)} transacciones`}
        tone="emerald"
      />
      <Celda
        icon={Banknote}
        label="Cobrado en efectivo"
        value={formatCurrencyAdaptive(consolidado.cobrado_efectivo, 100_000)}
        hint="Según ventas registradas"
        tone="ink"
      />
      <Celda
        icon={CreditCard}
        label="Otros medios de pago"
        value={formatCurrencyAdaptive(consolidado.cobrado_no_efectivo, 100_000)}
        hint="Tarjeta, transferencia, crédito…"
        tone="ink"
      />
      <Celda
        icon={Receipt}
        label="Gastos de turnos"
        value={formatCurrencyAdaptive(consolidado.gastos, 100_000)}
        hint={`Bóvedas: ${formatCurrencyAdaptive(consolidado.bovedas, 100_000)}`}
        tone="amber"
      />
      <Celda
        icon={Fuel}
        label="Despacho PTS"
        value={`${formatNumber(consolidado.galones_pts)} gal`}
        hint={`Lados asignados · ${formatCurrencyAdaptive(consolidado.monto_pts, 100_000)}`}
        tone="brand"
      />
    </div>
  );
}
